import { useFormik } from "formik";
import * as Yup from "yup";
import { Calculator } from "lucide-react";
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Label,
  RadioGroup,
  RadioGroupItem,
  Input,
  Button,
  Checkbox,
} from "src/shadcn/components/ui";
import { NumericFormat } from "react-number-format";
import { useState } from "react";
import { catchAsync, convertArrayToOptionType, IS_SUCCESS_RESPONSE, optionTypes, PHONE_NUMBER_REGEX } from "src/helpers";
import { axiosInstance } from "src/services/axiosSetup";
import LoadingDialog from "src/shadcn/components/custom/LoadingDialog";
import { API_RESP_INIT, RATINGS } from "./modules";
import CustomeDialog from "src/shadcn/components/custom/CustomeDialog";
import { useMainContext } from "src/pages/MYTReportCopy/Components/MainContext";
import Dropdown from "src/shadcn/components/custom/Dropdown";
import { MonthYearPicker } from "src/shadcn/components/custom/MonthYearPicker";


const MONTHS = [
  'JAN', 'FEB', 'MAR', 'APR', 'MAY', 'JUN',
  'JUL', 'AUG', 'SEP', 'OCT', 'NOV', 'DEC'
];

const validationSchema = Yup.object({
  consumerNumber: Yup.string().required("Consumer Number is required"),
  monthYear: Yup.string().required("Month Year is required"),
  customerCategory: Yup.string().required("Customer category is required"),
  industryType: Yup.string().required("industryType is required"),
  isEHV: Yup.boolean(),
  todZoneA: Yup.number()
    .required("ToD Zone A is required")
    .min(0, "Value must be positive")
    .integer("Value must be an integer"),
  todZoneB: Yup.number()
    .required("ToD Zone B is required")
    .min(0, "Value must be positive")
    .integer("Value must be an integer"),
  todZoneC: Yup.number()
    .required("ToD Zone C is required")
    .min(0, "Value must be positive")
    .integer("Value must be an integer"),
  todZoneD: Yup.number()
    .required("ToD Zone D is required")
    .min(0, "Value must be positive")
    .integer("Value must be an integer"),
  facRate: Yup.number().required("FAC Rate is required"),
  // .min(0.65, "Value more than 0.65"),
  taxRate: Yup.number().required("FAC Rate is required"),
  // .min(0.18, "Value more than 0.18"),
  contractDemand: Yup.string().required("Enter Contract Demand"),
  consumptions: Yup.string().required("Enter consumptions"),
});

type Props = {

};

const DUTY_TYPE_OPTIONS: optionTypes[] = [
  { label: "Yes (7.5)", value: "7.5" },
  { label: "No (0.0)", value: "0" },
]

const FORM_INIT_WITH_VALUES = {
  consumerNumber: "",
  monthYear: `${new Date().getMonth()}-${new Date().getFullYear().toString().substring(2)}`,
  customerCategory: "Industrial",
  industryType: "other",
  contractDemand: 65000,
  consumptions: null,
  isEHV: true,
  todZoneA: 1334100,
  todZoneB: 1334100,
  todZoneC: 1334100,
  todZoneD: 1334100,
  facRate: 0.65,
  taxRate: 0.18,
  rating: "",
  dutyPercentage: "0",
};

const FORM_INIT_VALUES = {
  consumerNumber: "",
  monthYear: `${new Date().getMonth()}-${new Date().getFullYear().toString().substring(2)}`,
  customerCategory: "Industrial",
  industryType: "other",
  contractDemand: null,
  consumptions: null,
  isEHV: true,
  todZoneA: null,
  todZoneB: null,
  todZoneC: null,
  todZoneD: null,
  facRate: 0.65,
  taxRate: 0.18,
  rating: "",
  dutyPercentage: "0",
}

export function CalculatorFormForModel({ }: Props) {

  const { updateApiRespData, updateFormData, showFormDialog, updateShowFromDialog } = useMainContext()

  const formik = useFormik({
    initialValues: FORM_INIT_VALUES,
    validationSchema,
    onSubmit: (values) => {
      console.log("Form submitted:", values);
      // Handle form submission here

      const [month, year] = values.monthYear.split("-");

      const postData = {
        consumerNumber: values.consumerNumber,
        monthYear: `${MONTHS[month as keyof object]}-${year}`,
        // monthRange:"Apr-Sep",
        customerCategory: values?.customerCategory,
        industryType: values?.industryType,
        contractDemand: values?.contractDemand,
        consumptions: values?.consumptions,
        isEHVCategoryCustomer: values?.isEHV,
        zoneA: values?.todZoneA,
        zoneB: values?.todZoneB,
        zoneC: values?.todZoneC,
        zoneD: values?.todZoneD,
        facRate: values.facRate,
        taxRate: values.taxRate,
        rating: values.rating == "" ? null : values.rating,
        dutyPercentage: Number(values.dutyPercentage),

      };

      // formikData(postData);
      updateFormData(postData)
      handleSendData(postData);
    },
  });

  const [isLoading, setisLoading] = useState<boolean>(false);
  const [isFormDiaglogOpen, setisFormDiaglogOpen] = useState<boolean>(false);

  const handleSendData = catchAsync(
    async (postData: any) => {
      setisLoading(true);
      // getData(API_RESP_INIT);
      return await axiosInstance.post("/report/project-impact", {
        ...postData,
      });
    },
    (result: any) => {
      if (IS_SUCCESS_RESPONSE(result)) {
        setisLoading(false);
        // getData(result?.data);
        updateApiRespData(result?.data)
        updateShowFromDialog(false)
      }
    },
    () => {
      setisLoading(false);
    }
  );

  return (
    <div className="h-auto  ">
      <CustomeDialog className="top-[55%]" isOpen={showFormDialog} headerTitle={'Interactive Calculator – Estimate Your Impact'} hideDefaultCancle hideDefaultSubmit >
        <Card className="h-auto border-0 shadow-none ">
          {/* <CardHeader className="pb-4">
            <CardTitle className="flex items-center gap-2 text-md">
              <Calculator className="h-5 w-5 text-orange-500" />
              
            </CardTitle>
          </CardHeader> */}
          <CardContent className="space-y-6">
            <form onSubmit={formik.handleSubmit} className="space-y-6">

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-3 block">Enter Consumer Number</Label>
                  <NumericFormat
                    type="text"
                    // placeholder="0.00"
                    style={{ height: "40px" }}
                    name={"consumerNumber"}
                    value={formik.values?.consumerNumber}
                    // thousandsGroupStyle="lakh"
                    // thousandSeparator=","
                    className="flex-1 focus:outline-none border p-1 w-full rounded-md"
                    onBlur={formik.handleBlur}
                    onValueChange={(values: any) => {
                      formik.setFieldValue("consumerNumber", values?.floatValue?.toString()?.replace(PHONE_NUMBER_REGEX,""));
                    }}
                  />
                  {formik.touched.consumerNumber && formik.errors.consumerNumber && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.consumerNumber}</p>
                  )}
                </div>
              </div>

              <div className="grid lg:grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-3 block">Select Month Year:</Label>
                  {/* <RadioGroup
                    className="flex gap-2"
                    value={formik.values.monthYear}
                    onValueChange={(value) => formik.setFieldValue("monthYear", value)}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Apr-Sep" id="Apr-Sep" />
                      <Label htmlFor="Apr-Sep">Apr–Sep</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Oct-Mar" id="Oct-Mar" />
                      <Label htmlFor="Oct-Mar">Oct–Mar</Label>
                    </div>
                  </RadioGroup> */}
                  <MonthYearPicker value={formik.values.monthYear ? { month: Number(formik.values.monthYear.split("-")[0]), year: Number(`20${formik.values.monthYear.split("-")[1]}`) } : { month: new Date().getMonth(), year: new Date().getFullYear() }} onChange={({ month, year }: any) => {
                    console.log({ month, year });
                    formik.setFieldValue("monthYear", `${month}-${(year.toString())?.substring(2)}`)

                  }} />
                  {formik.touched.monthYear && formik.errors.monthYear && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.monthYear}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-3 block">Select Customer Category:</Label>
                  <RadioGroup
                    className="flex gap-2"
                    value={formik.values.customerCategory}
                    onValueChange={(value: any) => formik.setFieldValue("customerCategory", value)}
                  >
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Industrial" id="Industrial" />
                      <Label htmlFor="Industrial">Industrial</Label>
                    </div>
                    <div className="flex items-center space-x-2">
                      <RadioGroupItem value="Commercial" id="Commercial" />
                      <Label htmlFor="Commercial">Commercial</Label>
                    </div>
                  </RadioGroup>
                  {formik.touched.customerCategory && formik.errors.customerCategory && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.customerCategory}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-3 block">Select Industry Type</Label>
                  <Dropdown options={convertArrayToOptionType(["other", "textiles"])} onChange={(e: any) => { formik.setFieldValue("industryType", e) }} value={formik.values?.industryType} />
                  {formik.touched.industryType && formik.errors.industryType && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.industryType}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-3 block">Enter Billed Demand (kVA)</Label>
                  <NumericFormat
                    type="text"
                    placeholder="0.00"
                    style={{ height: "40px" }}
                    name={"contractDemand"}
                    value={formik.values?.contractDemand}
                    thousandsGroupStyle="lakh"
                    thousandSeparator=","
                    className="flex-1 focus:outline-none border p-1 w-full rounded-md"
                    onBlur={formik.handleBlur}
                    onValueChange={(values: any) => {
                      formik.setFieldValue("contractDemand", values?.floatValue);
                    }}
                  />
                  {formik.touched.contractDemand && formik.errors.contractDemand && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.contractDemand}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-3 block">Enter Consumption</Label>
                  <NumericFormat
                    type="text"
                    placeholder="0.00"
                    name={"consumptions"}
                    style={{ height: "40px" }}
                    value={formik.values?.consumptions}
                    thousandsGroupStyle="lakh"
                    thousandSeparator=","
                    className="flex-1 focus:outline-none border p-1 w-full rounded-md"
                    onBlur={formik.handleBlur}
                    onValueChange={(values: any) => {
                      formik.setFieldValue("consumptions", values?.floatValue);
                    }}
                  />
                  {formik.touched.consumptions && formik.errors.consumptions && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.consumptions}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-3 block">Select your Rating</Label>
                  <Dropdown options={RATINGS} onChange={(e: any) => { formik.setFieldValue("rating", e) }} value={formik.values?.rating} />
                  {formik.touched.rating && formik.errors.rating && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.rating}</p>
                  )}
                </div>
              </div>

              <div className="grid grid-cols-3 gap-4">
                <div>
                  <Label className="text-xs font-medium mb-3 block">
                    Enter FAC rate <span className="text-gray-400">(paisa/unit)</span>{" "}
                  </Label>
                  <NumericFormat
                    type="text"
                    placeholder="0.00"
                    name={"facRate"}
                    value={formik.values?.facRate}
                    thousandsGroupStyle="lakh"
                    thousandSeparator=","
                    className="flex-1 focus:outline-none border p-1 w-full rounded-md"
                    onBlur={formik.handleBlur}
                    onValueChange={(values: any) => {
                      formik.setFieldValue("facRate", values?.floatValue);
                    }}
                  />
                  {formik.touched.facRate && formik.errors.facRate && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.facRate}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-3 block">
                    Enter TAX rate <span className="text-gray-400">(paisa/unit)</span>
                  </Label>
                  <NumericFormat
                    type="text"
                    placeholder="0.00"
                    name={"taxRate"}
                    value={formik.values?.taxRate}
                    thousandsGroupStyle="lakh"
                    thousandSeparator=","
                    className="flex-1 focus:outline-none border p-1 w-full rounded-md"
                    onBlur={formik.handleBlur}
                    onValueChange={(values: any) => {
                      formik.setFieldValue("taxRate", values?.floatValue);
                    }}
                  />
                  {formik.touched.taxRate && formik.errors.taxRate && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.taxRate}</p>
                  )}
                </div>
                <div>
                  <Label className="text-xs font-medium mb-3 block">Select Duty Percentage</Label>
                  <Dropdown options={DUTY_TYPE_OPTIONS} onChange={(e: any) => { formik.setFieldValue("dutyPercentage", Number(e)) }} value={(formik.values?.dutyPercentage).toString()} />
                  {formik.touched.dutyPercentage && formik.errors.dutyPercentage && (
                    <p className="text-sm text-red-500 mt-1">{formik.errors.dutyPercentage}</p>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <Checkbox
                  id="ehv"
                  checked={formik.values.isEHV}
                  onCheckedChange={(checked) => formik.setFieldValue("isEHV", checked)}
                />
                <Label htmlFor="ehv">I am an EHV category customer</Label>
              </div>

              <div className="grid lg:grid-cols-4 gap-4">
                {[
                  { label: "ToD Zone A:", key: "todZoneA" },
                  { label: "ToD Zone B:", key: "todZoneB" },
                  { label: "ToD Zone C:", key: "todZoneC" },
                  { label: "ToD Zone D:", key: "todZoneD" },
                ].map((zone) => (
                  <div key={zone.key}>
                    <div className="">
                      <Label className="w-20 text-xs font-medium">{zone.label} <span className="text-gray-500"> (units)</span></Label>
                      <div className="w-full items-center border p-2">
                        <NumericFormat
                          type="text"
                          name={zone.key}
                          value={formik.values[zone.key as keyof typeof formik.values] as any}
                          thousandsGroupStyle="lakh"
                          thousandSeparator=","
                          className="w-full focus:outline-none"
                          onBlur={formik.handleBlur}
                          placeholder="0.00"
                          onValueChange={(values: any) => {
                            formik.setFieldValue(zone.key, values?.floatValue);
                          }}
                        />
                        {/* <span className="text-sm text-muted-foreground">units</span> */}
                      </div>
                    </div>
                    {formik.touched[zone.key as keyof typeof formik.touched] &&
                      formik.errors[zone.key as keyof typeof formik.errors] && (
                        <p className="text-sm text-red-500 mt-1 ml-23">
                          {formik.errors[zone.key as keyof typeof formik.errors]}
                        </p>
                      )}
                  </div>
                ))}
              </div>

              <div className="flex space-x-2">
                <Button type="button" variant={'outlinePrimary'} onClick={() => updateShowFromDialog(false)} className="w-full" disabled={isLoading}>
                  Cancel
                </Button>
                <Button type="submit" className="w-full" disabled={isLoading}>
                  Calculate Impact
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </CustomeDialog>
      <LoadingDialog isOpen={isLoading} />
    </div>
  );
}
